import { useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import {
  Logout,
  Person,
  SettingsInputComposite,
  StackedLineChart,
  Percent,
  AccountBalance,
  Inventory,
  Assignment,
  DoDisturb,
  SettingsApplications,
  QueryStats,
  Note,
  ReceiptLong,
  Archive,
  ExpandLess,
  ExpandMore,
  Settings,
  ShowChart,
} from "@mui/icons-material";
import { LOGO_URL, LOGO_URL_SMALL } from "config";

import { useAppDispatch, useAuth, useLayout } from "features/store";

import { useLogoutMutation } from "features/auth/auth";
import { useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import {
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { toggleDrawer } from "features/layout/layoutSlice";
import ViewListIcon from "@mui/icons-material/ViewList";
import { OBSOLETE_PAGES_NAV_LABEL, UrlPaths } from "constants/index";
import { SETTINGS_TAB_PERMISSIONS } from "features/auth/permissions";

// Nav rows are deliberately tighter than the MUI default (48px row, 56px icon
// gutter): the drawer is a fixed 100vh with overflow hidden, so at the default
// sizing the 13 entries pushed Logout off the bottom below a ~760px viewport.
const ROW_SX = {
  minHeight: 38,
  px: 2,
  py: 0.5,
};

const SCROLL_SX = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: 6 },
};

const ROW_TEXT_PROPS = { noWrap: true, fontSize: 14 };

// Collapsed, the row itself does the centring: ListItemIcon is exactly the icon's
// width once minWidth is 0, so it has no space of its own to centre within.
const rowSx = (drawerOpen: boolean, indent = false) => ({
  ...ROW_SX,
  justifyContent: drawerOpen ? "flex-start" : "center",
  px: drawerOpen ? 2 : 0,
  ...(drawerOpen && indent ? { pl: 3.5 } : {}),
});

const iconSx = (drawerOpen: boolean) => ({
  minWidth: 0,
  mr: drawerOpen ? 1.5 : 0,
});

const MENU = [
  { icon: <HomeIcon />, text: "Home", link: "/" },
  { icon: <Inventory />, text: "Strategies", link: "/stock" },
  { icon: <Assignment />, text: "Orders", link: "/orders" },
  { icon: <ReceiptLong />, text: "Transactions", link: "/transactions" },
  { icon: <Person />, text: "Clients", link: "/clients" },
  { icon: <AccountBalance />, text: "Accounts", link: "/accounts" },
  { icon: <ShowChart />, text: "Securities", link: UrlPaths.SECURITIES },
  { icon: <SettingsApplications />, text: "Management", link: "/management" },
  { icon: <DoDisturb />, text: "Non Optic", link: "/non_optic" },
  { icon: <ViewListIcon />, text: "Option Chain", link: "/option_chain" },
  { icon: <Note />, text: "Reports", link: UrlPaths.REPORT },
];

// Shown only to a user holding at least one Settings tab permission: anyone
// else would find no tab there and be sent Home.
const SETTINGS_ROW = {
  icon: <Settings />,
  text: "Settings",
  link: UrlPaths.SETTINGS,
};

// Pages nobody uses any more. They keep their own rows (Div Optic and Growth
// Optic have always shared the Composite route) but live below a divider inside
// a collapsible group, and each target page carries the
// OBSOLETE_PAGE_DISCLAIMER banner via Layout.
const OBSOLETE_MENU = [
  { icon: <SettingsInputComposite />, text: "Composite", link: "/composite" },
  { icon: <Percent />, text: "Div Optic", link: "/composite" },
  { icon: <StackedLineChart />, text: "Growth Optic", link: "/composite" },
  {
    icon: <QueryStats />,
    text: "Old Reports",
    link: UrlPaths.REPORT_DEPRECATED,
  },
];

// Nav rows navigate with href, so every click is a full page load and React
// state does not survive it: the group's open/closed choice is remembered per
// browser instead. Absent or unreadable storage means closed, which is the
// point of the group (the four rows are what crowded the drawer).
const OBSOLETE_OPEN_STORAGE_KEY = "opticam.sideMenu.obsoletePagesOpen";

const readObsoleteOpen = () => {
  try {
    return window.localStorage.getItem(OBSOLETE_OPEN_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeObsoleteOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(OBSOLETE_OPEN_STORAGE_KEY, String(open));
  } catch {
    // Private-mode / disabled storage: the group still toggles for this page.
  }
};

const SideMenu = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const permissions: string[] = user?.permissions ?? [];
  const showSettings = SETTINGS_TAB_PERMISSIONS.some((permission) =>
    permissions.includes(permission),
  );
  const { drawerOpen, drawerWidth } = useLayout();
  const dispatch = useAppDispatch();

  const [logout] = useLogoutMutation();

  const onLogout = () => {
    logout();
  };

  const handleDrawerToggle = () => {
    dispatch(toggleDrawer());
  };

  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Sitting on one of these pages with the group shut would hide where you are,
  // so the current route wins over the remembered choice.
  const onObsoletePage = OBSOLETE_MENU.some((item) => item.link === pathname);
  const [obsoleteOpen, setObsoleteOpen] = useState(
    () => onObsoletePage || readObsoleteOpen(),
  );

  // Collapsed to the icon rail there is no room to reveal four labelled rows,
  // so the archive icon opens the drawer and the group in one click.
  const handleObsoleteToggle = () => {
    if (!drawerOpen) {
      dispatch(toggleDrawer());
      setObsoleteOpen(true);
      writeObsoleteOpen(true);
      return;
    }
    setObsoleteOpen(!obsoleteOpen);
    writeObsoleteOpen(!obsoleteOpen);
  };

  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  const renderRow = (
    {
      icon,
      text,
      link,
    }: {
      icon: JSX.Element;
      text: string;
      link: string;
    },
    indent = false,
  ) => (
    <ListItem key={text} disablePadding>
      <ListItemButton href={link} sx={rowSx(drawerOpen, indent)}>
        <ListItemIcon sx={iconSx(drawerOpen)}>{icon}</ListItemIcon>
        <ListItemText
          primary={text}
          primaryTypographyProps={ROW_TEXT_PROPS}
          hidden={!drawerOpen}
        />
      </ListItemButton>
    </ListItem>
  );

  const drawer = (
    <Box
      display={"flex"}
      flexDirection={"column"}
      height={"100vh"}
      overflow={"hidden"}
    >
      <Box height={65} justifyContent={"center"} display={"flex"}>
        <IconButton onClick={handleDrawerToggle}>
          <img
            width={drawerOpen ? 150 : 50}
            src={drawerOpen ? LOGO_URL : LOGO_URL_SMALL}
          />
        </IconButton>
      </Box>
      <Divider />
      <Box flexGrow={1} sx={SCROLL_SX}>
        <List disablePadding sx={{ py: 0.5 }}>
          {MENU.map((item) => renderRow(item))}
          {showSettings && renderRow(SETTINGS_ROW)}
        </List>
        <Divider sx={{ my: 0.5 }} />
        <List disablePadding sx={{ pb: 0.5 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleObsoleteToggle}
              sx={rowSx(drawerOpen)}
              aria-expanded={drawerOpen && obsoleteOpen}
              aria-label={OBSOLETE_PAGES_NAV_LABEL}
            >
              <ListItemIcon sx={iconSx(drawerOpen)}>
                <Archive />
              </ListItemIcon>
              <ListItemText
                primary={OBSOLETE_PAGES_NAV_LABEL}
                primaryTypographyProps={ROW_TEXT_PROPS}
                hidden={!drawerOpen}
              />
              {drawerOpen &&
                (obsoleteOpen ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                ))}
            </ListItemButton>
          </ListItem>
          <Collapse
            in={drawerOpen && obsoleteOpen}
            timeout="auto"
            unmountOnExit
          >
            <List disablePadding>
              {OBSOLETE_MENU.map((item) => renderRow(item, true))}
            </List>
          </Collapse>
        </List>
      </Box>
      <Divider />
      <List disablePadding sx={{ py: 0.5 }}>
        <ListItem key="logout" disablePadding>
          <ListItemButton onClick={onLogout} sx={rowSx(drawerOpen)}>
            <ListItemIcon sx={iconSx(drawerOpen)}>
              <Logout />
            </ListItemIcon>
            <ListItemText
              primary={"Logout"}
              primaryTypographyProps={ROW_TEXT_PROPS}
              hidden={!drawerOpen}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: drawerWidth },
        flexShrink: { sm: 0 },
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default SideMenu;
